import { boolean, number, object, ObjectSchema, string } from 'yup';

import { InsurancePolicy } from '@/types/insurancePolicy';
import { transformEmptyStringToNull } from '@/utils/transformEmptyStringToNull';

const transformDollarsToCents = (value: string) => (value ? parseInt(value, 10) * 100 : value);
const convertToDecimalPercentage = (value: string) => {
  return value ? parseFloat(value) / 100 : value;
};

export const insuranceSectionSchema: ObjectSchema<
  Omit<
    InsurancePolicy,
    | 'insurance_card_images'
    | 'is_default'
    | 'is_in_network'
    | 'is_mutable'
    | 'is_self_pay'
    | 'is_waitlisted'
    | 'last_verification'
    | 'plan'
    | 'provider_name'
    | 'treatability_status'
    | 'has_active_treatability_match'
    | 'external_policy_verifications'
    | 'verifications'
    | 'has_policyholder_attrs'
    | 'prior_auth_required'
    | 'appointment_restriction_reasons'
  >
> = object().shape({
  annual_deductible: number().notRequired().label('Insurance Policy annual deductible'),
  carrier: string()
    .trim()
    .nonNullable()
    .label('Insurance Policy carrier')
    .transform(transformEmptyStringToNull),
  co_insurance: number()
    .notRequired()
    .label('Insurance Policy co insurance')
    .transform(convertToDecimalPercentage),
  co_pay: number()
    .notRequired()
    .label('Insurance Policy co pay')
    .transform(transformDollarsToCents),
  co_pay_unit: string().trim().notRequired().label('Insurance Policy co pay unit'),
  group_id: string()
    .trim()
    .notRequired()
    .label('Insurance Policy group id')
    .transform(transformEmptyStringToNull),
  guarantor: string()
    .strict(false)
    .trim()
    .notRequired()
    .label('Insurance Policy guarantor')
    .transform(transformEmptyStringToNull),
  health_plan_id: string()
    .trim()
    .notRequired()
    .label('Insurance Policy health plan id')
    .transform(transformEmptyStringToNull),
  id: string().optional().label('Insurance Policy id'),
  insurance_card_images: object()
    .shape({
      back_image_url: string().trim().notRequired().label('Insurance Policy back'),
      front_image_url: string().trim().notRequired().label('Insurance Policy front')
    })
    .optional()
    .default(undefined),
  insurer_address_line_1: string()
    .trim()
    .notRequired()
    .label('Insurance Policy address line 1')
    .transform(transformEmptyStringToNull),
  insurer_address_line_2: string()
    .trim()
    .notRequired()
    .label('Insurance Policy address line 2')
    .transform(transformEmptyStringToNull),
  insurer_city: string()
    .trim()
    .notRequired()
    .label('Insurance Policy city')
    .transform(transformEmptyStringToNull),
  insurer_state: string()
    .trim()
    .notRequired()
    .label('Insurance Policy state')
    .transform(transformEmptyStringToNull),
  insurer_country: string()
    .trim()
    .notRequired()
    .label('Insurance Policy country')
    .transform(transformEmptyStringToNull),
  insurer_zip_code: string()
    .trim()
    .notRequired()
    .label('Insurance Policy zip code')
    .transform(transformEmptyStringToNull),
  out_of_pocket_maximum: number()
    .notRequired()
    .label('Insurance Policy out of pocket maximum')
    .transform(transformDollarsToCents),
  person_id: string().trim().optional().label('Insurance Policy person id'),
  plan_healthie_id: string().trim().optional().nullable().label('Insurance Policy plan'),
  plan_end_date: string().trim().notRequired().label('Insurance Policy plan end date'),
  plan_start_date: string().trim().notRequired().label('Insurance Policy plan start date'),
  policyholder_dob: string()
    .trim()
    .notRequired()
    .label('Insurance Policy policyholder date of birth'),
  policyholder_legal_name: string()
    .trim()
    .notRequired()
    .label('Insurance Policy policyholder legal name'),
  policyholder_first_name: string()
    .trim()
    .notRequired()
    .label('Insurance Policy policyholder first name'),
  policyholder_last_name: string()
    .trim()
    .notRequired()
    .label('Insurance Policy policyholder last name'),
  priority_type: string().trim().notRequired().label('Insurance Policy priority type'),
  provider_phone_number: string()
    .trim()
    .notRequired()
    .label('Insurance Policy provider phone number')
    .transform(transformEmptyStringToNull),
  relationship: string().trim().notRequired().label('Insurance Policy relationship'),
  rx_bin: string()
    .trim()
    .notRequired()
    .label('Insurance Policy rx bin')
    .transform(transformEmptyStringToNull),
  rx_group: string()
    .trim()
    .notRequired()
    .label('Insurance Policy rx group')
    .transform(transformEmptyStringToNull),
  policy_member_id: string().trim().notRequired().label('Insurance Policy policy member id'),
  subscriber_id: string().trim().notRequired().label('Insurance Policy subscriber id'),
  type: string().trim().notRequired().label('Insurance Policy type'),
  was_discovered: boolean().optional(),
  _delete: boolean().optional()
});
